/**
 * How a tool call is DESCRIBED in the chat transcript (17.08.2026).
 *
 * Extracted from a 40-branch if-chain that had grown inside ToolCallCard, in
 * AIChatPanel.js. Two reasons it moved:
 *
 *  1. It was incomplete and silently so. Eleven tools had no branch and fell
 *     through to a truncated `JSON.stringify(input)` — including
 *     `terminal_run_command`, so "run this on your production server" rendered
 *     as `{"conn_id":"c1","command":"rm -rf …` with the command cut off at 60
 *     characters. A map plus the test in aiToolPresentation.test.js (every
 *     schema name must have an entry) makes that gap impossible to reintroduce.
 *  2. Presentation kept accreting in a 2,300-line component.
 *
 * This is deliberately NOT merged into AIChatPanel's TOOL_REGISTRY: that map
 * is about execution semantics (does this tool end the loop, does it need a
 * confirm gate) and is sparse on purpose — only tools with special behaviour
 * appear. This one must be exhaustive. Keeping them apart keeps "every tool
 * needs a label" checkable without asserting anything false about behaviour.
 */

/** Trim a free-text value to something that fits one line of the card. */
const clip = (value, max = 60) => {
  const text = String(value ?? '').trim().replace(/\s+/g, ' ');
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

/** First non-empty of the given inputs, else the fallback. */
const firstOf = (input, keys, fallback) => {
  for (const key of keys) {
    const value = input?.[key];
    if (value !== undefined && value !== null && String(value).trim()) return clip(value);
  }
  return fallback;
};

/** Display names for generate_code's language enum (curl → cURL, not Curl). */
const CODEGEN_LANGUAGE_LABELS = {
  curl: 'cURL',
  python: 'Python',
  typescript: 'TypeScript',
  go: 'Go',
  java: 'Java',
  csharp: 'C#',
  php: 'PHP',
  ruby: 'Ruby',
  swift: 'Swift',
};

/**
 * tool name → (input) => one-line summary.
 *
 * Every tool in services/ai/toolSchemas.js must appear here. Read-only lookups
 * use the "Fetching …" present participle; actions name their target.
 */
export const TOOL_LABELS = {
  // ── Requests & collections ───────────────────────────────────────────────
  send_request: (i) => {
    if (i?.use_current_tab) return 'Resend active tab';
    if (i?.request_id) return `Saved request ${clip(i.request_id, 24)}`;
    return clip(`${i?.method || 'GET'} ${i?.url || ''}`.trim());
  },
  get_collections: () => 'Fetching collections',
  get_request: (i) => `Request ${clip(i?.request_id, 24)}`,
  get_current_request: () => 'Reading the active tab',
  create_collection: (i) => firstOf(i, ['name'], 'New collection'),
  create_folders: (i) => {
    const items = Array.isArray(i?.folders) ? i.folders : [];
    if (items.length === 1) return firstOf(items[0], ['name'], 'New folder');
    return `${items.length} folder(s)`;
  },
  create_request: (i) => clip(`${i?.method || 'GET'} ${i?.name || i?.url || ''}`.trim()),
  move_requests: (i) => `Move ${i?.moves?.length || 0} request(s)`,
  update_request: (i) => `Update request ${clip(i?.request_id, 24)}`,
  run_collection: (i) => firstOf(i, ['collection_name', 'collection_id'], 'Run collection'),
  get_last_response: () => 'Reading the last response',
  generate_tests: () => 'Generating tests',
  generate_pre_request_script: () => 'Writing a pre-request script',

  // ── Environments & variables ─────────────────────────────────────────────
  get_environments: () => 'Fetching environments',
  get_workspace_variables: () => 'Fetching workspace variables',
  get_global_variables: () => 'Fetching global variables',
  set_variable: (i) => `Set ${firstOf(i, ['key'], 'variable')}`,

  // ── Task boards ──────────────────────────────────────────────────────────
  get_boards: () => 'Fetching boards',
  get_board: (i) => firstOf(i, ['board_name', 'board_id'], 'Get board'),
  create_board: (i) => firstOf(i, ['name'], 'New board'),
  open_board: (i) => `Open ${firstOf(i, ['board_name', 'board_id'], 'board')}`,
  rename_board: (i) => `Rename board → ${firstOf(i, ['name'], '…')}`,
  delete_board: (i) => `Delete ${firstOf(i, ['board_name', 'board_id'], 'board')}`,
  create_list: (i) => firstOf(i, ['name'], 'New list'),
  create_task: (i) => firstOf(i, ['title'], 'New task'),
  update_task: (i) => (i?.done ? 'Mark task done' : `Update task ${clip(i?.task_id, 24)}`),
  write_task_description: (i) => `Write description ${clip(i?.task_id, 24)}`,

  // ── DB Manager ───────────────────────────────────────────────────────────
  db_list_connections: () => 'Fetching database connections',
  db_get_schema: (i) => `Schema ${firstOf(i, ['database', 'conn_id'], '')}`.trim(),
  db_run_sql: (i) => firstOf(i, ['sql'], 'Run SQL'),

  // ── Documents ────────────────────────────────────────────────────────────
  get_documents: () => 'Fetching documents',
  read_document: (i) => `Read ${clip(i?.document_id, 24)}`,
  create_doc_folder: (i) => firstOf(i, ['name'], 'New folder'),
  create_document: (i) => firstOf(i, ['title'], 'New document'),
  open_document: (i) => `Open ${clip(i?.document_id, 24)}`,
  rename_document: (i) => `Rename → ${firstOf(i, ['title'], '…')}`,
  delete_document: (i) => `Delete ${clip(i?.document_id, 24)}`,
  write_document: (i) => (i?.mode === 'append' ? 'Append to document' : 'Write document'),

  // ── Workspace: history, environments, auth ───────────────────────────────
  get_history: (i) => {
    if (i?.only_failures) return 'Fetching failed requests';
    if (i?.url_contains) return `History matching "${clip(i.url_contains, 30)}"`;
    if (i?.since_minutes) return `History, last ${i.since_minutes} min`;
    return 'Fetching request history';
  },
  create_environment: (i) => firstOf(i, ['name'], 'New environment'),
  duplicate_environment: (i) => `Duplicate → ${firstOf(i, ['new_name'], 'copy')}`,
  switch_environment: (i) => `Switch to ${firstOf(i, ['environment_name', 'environment_id'], 'environment')}`,
  // Never surface the credential itself — this line lands in the transcript.
  set_auth: (i) => `Set ${firstOf(i, ['type'], 'auth')} auth on ${clip(i?.request_id, 24) || 'request'}`,

  import_spec: (i) => (i?.url ? `Import ${clip(i.url, 48)}` : 'Import pasted spec'),

  // ── Realtime connections ─────────────────────────────────────────────────
  create_stream_connection: (i) => `New ${firstOf(i, ['kind'], 'stream')} connection`,

  // ── FTP ──────────────────────────────────────────────────────────────────
  ftp_list_connections: () => 'Fetching FTP connections',
  ftp_list_dir: (i) => `List ${firstOf(i, ['path'], '/')}`,

  // ── Git ──────────────────────────────────────────────────────────────────
  get_git_status: () => 'Checking Git sync status',

  // ── Mock server ──────────────────────────────────────────────────────────
  create_mock_example: (i) => (
    i?.source === 'literal'
      ? `Mock ${firstOf(i, ['name'], 'response')}`
      : 'Mock the last response'
  ),

  // ── Code generation ──────────────────────────────────────────────────────
  generate_code: (i) => {
    const lang = CODEGEN_LANGUAGE_LABELS[String(i?.language || '').toLowerCase()]
      || firstOf(i, ['language'], 'code');
    return `Generate ${lang}`;
  },

  // ── Web discovery ────────────────────────────────────────────────────────
  web_search: (i) => (i?.query ? `Search "${clip(i.query, 48)}"` : 'Web search'),
  fetch_url: (i) => (i?.url ? `Fetch ${clip(i.url, 48)}` : 'Fetch page'),

  // ── Terminal ─────────────────────────────────────────────────────────────
  terminal_list_connections: () => 'Fetching SSH connections',
  // The command itself is the only thing worth showing — this is the tool that
  // runs code on someone's server, and it used to render as truncated JSON.
  terminal_run_command: (i) => firstOf(i, ['command'], 'Run command'),
};

/**
 * One-line description of a tool call for the transcript.
 *
 * An unknown tool still gets something readable rather than raw JSON: a model
 * can hallucinate a tool name, and a name we don't recognise is exactly when a
 * legible card matters most.
 */
export function describeToolCall(toolName, toolInput) {
  const label = TOOL_LABELS[toolName];
  if (!label) return clip(toolName || 'Unknown tool', 60);
  try {
    const text = label(toolInput || {});
    return text && String(text).trim() ? text : clip(toolName, 60);
  } catch {
    // A malformed input from the model must never break the transcript.
    return clip(toolName, 60);
  }
}
